import type { Action } from "@judo/actions";
import { useElementDisabled, useElementVisibility } from "@judo/core";
import { useConfirmation } from "@judo/feedback";
import { useModelLabel } from "@judo/i18n";
import type { Button as ButtonElement, ButtonGroup as ButtonGroupElement, PageDefinition } from "@judo/model-api";
import { getButtonGroupTestId, getButtonTestId } from "@judo/test-ids";
import { Button, ButtonGroup } from "@mui/material";
import { getDestructiveConfirmationOptions, isDestructiveAction } from "../utils/destructive-action-utils";
import { findActionByDefinition } from "../utils/find-action-by-definition";
import { getVisibleButtons } from "../utils/get-visible-buttons";
import { IconRenderer } from "./IconRenderer";

export interface ButtonGroupRendererProps {
	element: ButtonGroupElement;
	page?: PageDefinition;
	editMode?: boolean;
	onDispatch?: (action: Action) => Promise<unknown>;
}

/**
 * Renderer for ButtonGroup elements.
 * Buttons dispatch their associated Action from page.actions when clicked.
 */
/**
 * Map button style to MUI variant.
 * Page/dialog level buttons should never use "text" variant - force to "outlined".
 */
function getButtonVariant(buttonStyle?: string): "contained" | "outlined" | "text" {
	if (buttonStyle === "text") return "outlined";
	return (buttonStyle as "contained" | "outlined") ?? "contained";
}

export function ButtonGroupRenderer({ element, page, editMode, onDispatch }: ButtonGroupRendererProps) {
	const { confirm } = useConfirmation();
	const groupHidden = useElementVisibility(element);

	const handleClick = async (button: ButtonElement) => {
		// Find the corresponding Action from page.actions by matching actionDefinition
		const action = page ? findActionByDefinition(page, button.actionDefinition) : undefined;

		if (action && onDispatch) {
			const actionType = button.actionDefinition?.["@type"];
			if (isDestructiveAction(actionType)) {
				const confirmed = await confirm(getDestructiveConfirmationOptions(actionType!));
				if (!confirmed) return;
			}
			onDispatch(action as Action).catch((error) => {
				console.error("Button action failed:", error);
			});
		}
		// If no action found or no dispatcher, button click is a no-op
	};

	// Filter buttons to only show those with matching page actions
	// Pass editMode to control visibility of Update/Cancel buttons
	const visibleButtons = getVisibleButtons(element.buttons, page?.actions, { isEditMode: editMode });

	// Don't render empty button group
	if (groupHidden || visibleButtons.length === 0) {
		return null;
	}

	const groupTestId = getButtonGroupTestId(element);

	return (
		<ButtonGroup data-testid={groupTestId}>
			{visibleButtons.map((button) => {
				return <TranslatedButton key={button["xmi:id"]} button={button} onClick={() => handleClick(button)} />;
			})}
		</ButtonGroup>
	);
}

/** Renders a single button with a translated label via useModelLabel. Handles hiddenBy and enabledBy. */
function TranslatedButton({ button, onClick }: { button: ButtonElement; onClick: () => void }) {
	const hidden = useElementVisibility(button);
	const disabled = useElementDisabled(button);
	const { label } = useModelLabel(button);
	const buttonTestId = getButtonTestId(button);

	if (hidden) {
		return null;
	}

	return (
		<Button
			variant={getButtonVariant(button.buttonStyle)}
			onClick={onClick}
			disabled={disabled}
			data-testid={buttonTestId}
			startIcon={button.icon ? <IconRenderer icon={button.icon} /> : undefined}
		>
			{label}
		</Button>
	);
}
